import { action, extendObservable, observable } from "mobx";
import planPipelineFactory from "../types/planPipelineFactory";
import { calculateAngleByPoints } from "@dvsproj/ipat-core/geometryUtils";
import { getIsolatePipelineCircuitsWithFlowAndDrops } from "@dvsproj/ipat-core/pipelineUtils";

import {
  sprinklerLineErrors,
  driplineLineErrors,
  pressureTubingLineErrors,
} from "../../utils/pipelineErrorsUtils";
import { throttle } from "lodash";

/* eslint import/no-anonymous-default-export: [2, {"allowArrowFunction": true}] */
export default (plan, settingsState) => {
  const pipelineFactory = planPipelineFactory(plan, settingsState);

  const innerState = observable({
    pipelines: undefined,
    circuitsCalculatedAt: undefined,
    pipesModifiedAt: undefined,
  });

  extendObservable(plan, {
    cleanupPipelineCircuits() {
      innerState.pipelines = undefined;
      innerState.circuitsCalculatedAt = undefined;
      innerState.pipesModifiedAt = undefined;
    },
    recalculatePipelineCircuits: throttle(
      action(() => {
        const circuits = getIsolatePipelineCircuitsWithFlowAndDrops(plan);
        innerState.pipelines = circuits.map((c) => pipelineFactory(c));
        const drops = circuits.reduce(
          (acc, val) => acc.concat(val?.drops ?? []),
          []
        );
        plan.updateElementsDrop(drops);
        innerState.circuitsCalculatedAt = new Date();
      }),
      100
    ),
    updatePipesModifiedAt: action(() => {
      innerState.pipesModifiedAt = new Date();
    }),
    get pipelines() {
      return innerState.pipelines;
    },
    get pipelinesCalculatedMs() {
      return innerState.circuitsCalculatedAt;
    },
    get pipesModifiedAt() {
      return innerState.pipesModifiedAt;
    },
  });

  extendObservable(plan, {
    get pipelineColors() {
      return !innerState.pipelines
        ? null
        : [
          ...innerState.pipelines
            .filter((p) => !p.isStartPoint)
            .map((p) => p.color),
          "#9EA1A2",
        ].filter((v, i, a) => a.indexOf(v) === i);
    },
    addPipePoint: action((data, lineId) => {
      const point = plan.addElementToStorage({
        ...data,
        type: "pipeline-point",
      });
      const line = plan.pipes.find((l) => l.id === lineId);
      if (line != null) {
        plan.addElementToStorage({
          type: "pipe",
          start: point.id,
          end: line.end,
          color: line.color,
        });
        line.end = point.id;
      }
      plan.pipesModified = true;
      plan.updatePipesModifiedAt();
      return point;
    }),
    addPipe: action(({ start, end, color }) => {
      const pipes = plan.pipes.filter((pipe) => {
        return (
          (pipe.start === start && pipe.end === end) ||
          (pipe.end === start && pipe.start === end)
        );
      });
      if (pipes && pipes.length > 0) {
        return;
      }
      const result = plan.addElementToStorage({
        type: "pipe",
        start,
        end,
        color,
      });
      plan.pipesModified = true;
      plan.updatePipesModifiedAt();
      return result;
    }),
    addPipeline: (points) => {
      //sanityCheck
      const filteredPoints = [];
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        if (p.type != null) {
          filteredPoints.push(p);
        } else if (p.pointType === "dripline-point") {
          filteredPoints.push(p);
        } else {
          const count = points.filter((point) =>
            point.parentId === p.parentId &&
            point.x === p.x &&
            point.y === p.y
          ).length;
          if (count > 1) {
            filteredPoints.push(p);
          } else {
            let angle = 0;
            if (i > 0 && i < points.length - 1) {
              const prevPoint = points[i - 1];
              const nextPoint = points[i + 1];
              angle = calculateAngleByPoints(prevPoint, p, nextPoint);
            }
            if (angle < 175) {
              filteredPoints.push(p);
            }
          }
        }
      }

      const path = [];
      const shapePointsMap = new Map();
      for (const p of filteredPoints) {
        let point = null;
        if (p.parentId) {
          point = plan.pipePoints.find((e) => e.parentId === p.parentId);
        }

        if (!point && p.parentId) {
          point = shapePointsMap.get(p.parentId);
        }

        if (point == null) {
          let ptype = p.pointType;
          if (!ptype) {
            ptype = p.systemType === "valve-box" ? "start-point" : "l-point";
          }

          point = plan.addElementToStorage({
            type: "pipeline-point",
            pointType: ptype,
            x: p.x,
            y: p.y,
            //this should work only for valvebox
            parentId: p.parentId,
          });
          shapePointsMap.set(p.parentId, point);
        }
        path.push(point);
      }

      for (let i = 1; i < path.length; i++) {
        plan.addElementToStorage({
          type: "pipe",
          start: path[i - 1].id,
          end: path[i].id,
        });
      }

      plan.updatePipesModifiedAt();
    },
    addTubingTree: (branches) => {
      if (branches == null || branches.length === 0) {
        return;
      }

      branches.sort((a, b) => a.level - b.level);

      const shapePointsMap = new Map();
      for (const b of branches) {
        const path = [];
        for (const p of b.points) {
          let point = null;
          if (p.parentId) {
            point = plan.pipePoints.find((e) => e.parentId === p.parentId);
          }

          if (!point && p.parentId) {
            point = shapePointsMap.get(p.parentId);
          }

          if (!point && p.id) {
            point = shapePointsMap.get(p.id);
          }

          if (point == null) {
            let ptype = p.pointType;
            if (!ptype) {
              ptype = p.systemType === "valve-box" ? "start-point" : "l-point";
            }

            point = plan.addElementToStorage({
              id: p.id,
              type: "pipeline-point",
              pointType: ptype,
              x: p.x,
              y: p.y,
              parentId: p.parentId,
            });
            shapePointsMap.set(p.id, point);
          }
          path.push(point);
        }

        for (let i = 1; i < path.length; i++) {
          plan.addElementToStorage({
            type: "pipe",
            start: path[i - 1].id,
            end: path[i].id,
          });
        }
      }
      plan.updatePipesModifiedAt();
    },
    checkPipeElements: action(() => {
      //remove or move to parents
      const points = plan.elements.filter(
        (e) =>
          e.type === "pipeline-point" &&
          (e.pointType === "sprinkler-point" ||
            e.isSystemElementPoint ||
            e.isRzwsPoint ||
            e.isRaisedBedPoint)
      );
      points.forEach((p) => {
        const parent = p.sprinkler;
        if (!parent) {
          plan.removeElementById(p.id);
        } else if (parent.x !== p.x || parent.y !== p.y) {
          p.x = parent.x;
          p.y = parent.y;
        }
      });

      const pointsIds = points.map((point) => point.parentId);

      //add new sprinklers / rzws / raised beds
      const sprinklerElements = plan.elements.filter(
        (e) =>
          pointsIds.indexOf(e.id) < 0 &&
          (e.type === "sprinkler" ||
            e.type === "rzws" ||
            e.type === "raised-bed")
      );
      if (sprinklerElements && sprinklerElements.length > 0) {
        sprinklerElements.forEach((element) => {
          plan.addElementToStorage({
            x: element.x,
            y: element.y,
            pointType: `${element.type}-point`,
            type: "pipeline-point",
            parentId: element.id,
          });
        });
      }

      //add new systemElements
      [
        "water-supply",
        "water-tap-point",
        "water-meter",
        "water-filter",
        "fertilizer",
        "valve-box",
        "combi-box",
      ].forEach((type) => {
        const newElements = plan.systemElements.filter(
          (e) => e.systemType === type && pointsIds.indexOf(e.id) < 0
        );

        if (newElements && newElements.length > 0) {
          newElements.forEach((el) => {
            plan.addElementToStorage({
              x: el.x,
              y: el.y,
              pointType: type === "valve-box" ? "start-point" : type,
              type: "pipeline-point",
              parentId: el.id,
            });
          });
        }
      });

      plan.updatePipesModifiedAt();
    }),
    recalculateCables: action(
      async (planId, email, api, paths, recalculate = null) => {
        try {
          plan.elements.replace(
            plan.elements.filter(
              (e) =>
                e.type !== "irrigationValveCable" &&
                e.type !== "waterMeterCable"
            )
          );

          const valveboxes = plan.getSystemElementsByType("valve-box");
          const waterSupply = plan.getSystemElementsByType("water-supply")?.[0];
          const controller = plan.getSystemElementsByType("controller")?.[0];

          const trenchingResultObj = await api.trenchingWays(
            planId,
            email,
            plan.toJSON,
            [
              ...valveboxes.map((valvebox) => {
                return {
                  start: { x: valvebox.x, y: valvebox.y, id: valvebox.id },
                  end: { x: controller.x, y: controller.y, id: controller.id },
                  type: "irrigationValveCable",
                };
              }),
              {
                start: {
                  x: waterSupply.x,
                  y: waterSupply.y,
                  id: waterSupply.id,
                },
                end: { x: controller.x, y: controller.y, id: controller.id },
                type: "waterMeterCable",
              },
            ],
            paths
          );

          if (trenchingResultObj && trenchingResultObj.result) {
            const { result } = trenchingResultObj;
            if (result && result.length > 0) {
              result
                .filter((el) => {
                  return (
                    recalculate == null ||
                    (recalculate != null &&
                      recalculate.indexOf(el.way.type) >= 0)
                  );
                })
                .forEach((el, idx) => {
                  if (el && el.distance != null) {
                    plan.addElementToStorage({
                      type: el.way.type,
                      startId: el.way.start?.id,
                      stopId: el.way.stop?.id,
                      points: el.path.points,
                      distance: el.distance,
                    });
                  }
                });
              plan.updatePipesModifiedAt();
            }
          }
        } catch (e) {
          console.error(e);
        }
      }
    ),
    recalculatePipepoints: action(() => {
      plan.elements.replace(
        plan.elements.filter(
          (e) => (e.type !== "pipe" && e.type !== "pipeline-point")
        )
      );
      const sprinklers = plan.elements.filter(
        (e) =>
          e.type === "sprinkler" || e.type === "rzws" || e.type === "raised-bed"
      );
      if (sprinklers && sprinklers.length > 0) {
        sprinklers.forEach((element) => {
          plan.addElementToStorage({
            x: element.x,
            y: element.y,
            pointType: `${element.type}-point`,
            type: "pipeline-point",
            parentId: element.id,
          });
        });
      }

      plan.systemElements
        .filter(
          (elem) =>
            [
              "water-supply",
              "water-tap-point",
              "water-meter",
              "water-filter",
              "fertilizer",
              "valve-box",
              "combi-box",
            ].indexOf(elem.systemType) >= 0
        )
        .forEach((sysElem) => {
          plan.addElementToStorage({
            x: sysElem.x,
            y: sysElem.y,
            pointType:
              sysElem.systemType === "valve-box"
                ? "start-point"
                : sysElem.systemType,
            type: "pipeline-point",
            parentId: sysElem.id,
          });
        });
      plan.updatePipesModifiedAt();
    }),

    /**
     * Pipeline errors
     */
    pipelineError: null,
    pipelineErrorMessages(selectedElement, hoveredElement = null) {
      if (!hoveredElement) {
        plan.pipelineError = null;
        return [];
      }

      const selectedPipeline = selectedElement.pipelines
        ? selectedElement.pipelines[0]
        : undefined;

      const hoveredPipeline =
        hoveredElement && hoveredElement.pipelines
          ? hoveredElement.pipelines[0]
          : undefined;

      const labels = settingsState
        ? settingsState.texts.properties.pipeline
        : null;

      let errorMessages = [
        ...sprinklerLineErrors({
          hoveredElement,
          selectedElement,
          selectedPipeline,
          hoveredPipeline,
          labels,
          plan,
        }),
        ...driplineLineErrors({
          hoveredElement,
          selectedElement,
          selectedPipeline,
          hoveredPipeline,
          labels,
        }),
        ...pressureTubingLineErrors({
          hoveredElement,
          selectedElement,
          selectedPipeline,
          hoveredPipeline,
          labels,
        }),
      ];

      plan.pipelineError =
        plan.pipelineHasDrawing && errorMessages.length > 0
          ? errorMessages.map(({ text }) => text)[0]
          : null;

      return errorMessages;
    },

    /**
     * Changes the transparency of pipelines that are not available for connection
     * @param selectedElement
     */
    unconnectablePipelines: action((selectedElement) => {
      const selectedPipeline =
        selectedElement && selectedElement.pipelines
          ? selectedElement.pipelines[0]
          : undefined;

      const pipelineSetHidden = (pipeline, hasHidden = true) => {
        pipeline.sprinklers.forEach((sprinkler) => {
          if (sprinkler) sprinkler.setHidden(hasHidden);
        });
        pipeline.setHidden(hasHidden);
      };

      if (!plan.pipelines) {
        return;
      }

      if (selectedPipeline) {
        plan.pipelines.forEach((pipeline) => {
          if (pipeline.id !== selectedPipeline.id) {
            const labels = settingsState
              ? settingsState.texts.properties.pipeline
              : null;

            let errorMessages = [
              ...sprinklerLineErrors({
                selectedElement,
                selectedPipeline,
                hoveredPipeline: pipeline,
                labels,
                plan,
              }),
              ...driplineLineErrors({
                selectedElement,
                selectedPipeline,
                hoveredPipeline: pipeline,
                labels,
              }),
              ...pressureTubingLineErrors({
                selectedElement,
                selectedPipeline,
                hoveredPipeline: pipeline,
                labels,
              }),
            ];

            if (errorMessages.length > 0) {
              pipelineSetHidden(pipeline, true);
            }
          }
        });
      } else {
        plan.pipelines.map((p) => pipelineSetHidden(p, false));
      }
    }),
  });
  plan.recalculatePipelineCircuits();
  plan.updatePipesModifiedAt();
};
